/**
 * Landing Benefits organism component
 * Showcases the key benefits and value propositions of using Furniturr
 */

// #region Imports
import {
    Box,
    Typography,
    Container,
    useTheme,
    Stack,
    Card,
    CardContent,
    Avatar
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
    Savings,
    Speed,
    Nature,
    TrendingUp,
    Engineering,
    PrecisionManufacturing
} from "@mui/icons-material";
// #endregion

// #region Types
/**
 * Benefit item interface
 */
interface BenefitItem {
    id: string;
    icon: React.ReactNode;
    title: string;
    description: string;
    metric: string;
    color: "primary" | "secondary" | "success" | "warning" | "info" | "error";
}

/**
 * Props for LandingBenefits organism component
 */
interface LandingBenefitsOrganismProps {
    /** Optional test ID for testing purposes */
    "data-testid"?: string;
}
// #endregion

// #region Component
/**
 * LandingBenefits organism component showcasing key application benefits
 * Displays benefits in a visually appealing layout with metrics and icons
 *
 * @param props - The component props
 * @returns JSX element representing the benefits section
 */
function LandingBenefitsOrganism({ ...props }: LandingBenefitsOrganismProps) {
    const { t } = useTranslation('landing');
    const theme = useTheme();

    // Dynamic benefits data from translations
    const BENEFITS: BenefitItem[] = [
        {
            id: "cost-reduction",
            icon: <Savings />,
            title: t('benefits.items.costReduction.title'),
            description: t('benefits.items.costReduction.description'),
            metric: t('benefits.items.costReduction.metric'),
            color: "success"
        },
        {
            id: "time-efficiency",
            icon: <Speed />,
            title: t('benefits.items.timeEfficiency.title'),
            description: t('benefits.items.timeEfficiency.description'),
            metric: t('benefits.items.timeEfficiency.metric'),
            color: "primary"
        },
        {
            id: "waste-reduction",
            icon: <Nature />,
            title: t('benefits.items.wasteReduction.title'),
            description: t('benefits.items.wasteReduction.description'),
            metric: t('benefits.items.wasteReduction.metric'),
            color: "success"
        },
        {
            id: "precision-improvement",
            icon: <Engineering />,
            title: t('benefits.items.precisionImprovement.title'),
            description: t('benefits.items.precisionImprovement.description'),
            metric: t('benefits.items.precisionImprovement.metric'),
            color: "info"
        },
        {
            id: "productivity-boost",
            icon: <TrendingUp />,
            title: t('benefits.items.productivityBoost.title'),
            description: t('benefits.items.productivityBoost.description'),
            metric: t('benefits.items.productivityBoost.metric'),
            color: "warning"
        },
        {
            id: "material-versatility",
            icon: <PrecisionManufacturing />,
            title: t('benefits.items.materialVersatility.title'),
            description: t('benefits.items.materialVersatility.description'),
            metric: t('benefits.items.materialVersatility.metric'),
            color: "secondary"
        }
    ];

    return (
        <Box
            component="section"
            role="region"
            aria-labelledby="benefits-title"
            id="benefits"
            sx={{
                py: { xs: 8, md: 12 },
                backgroundColor: "background.paper",
                position: "relative",
                "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "50%",
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}05, ${theme.palette.secondary.main}05)`,
                    pointerEvents: "none",
                },
            }}
            {...props}
        >
            <Container maxWidth="lg">
                {/* Section Header */}
                <Box sx={{ textAlign: "center", mb: 8, position: "relative", zIndex: 1 }}>
                    <Typography
                        variant="overline"
                        sx={[
                            {
                                color: theme.palette.primary.main,
                                fontWeight: 600,
                                letterSpacing: 1,
                                mb: 2,
                                display: "block",
                            },
                            (theme) =>
                                theme.applyStyles("dark", {
                                    color: "rgba(255, 255, 255, 0.6)",
                                }),
                        ]}
                    >
                        {t('benefits.sectionTitle')}
                    </Typography>
                    <Typography
                        variant="h2"
                        component="h2"
                        id="benefits-title"
                        sx={{
                            mb: 3,
                            fontSize: { xs: "2rem", md: "3rem" },
                            fontWeight: 700,
                        }}
                    >
                        {t('benefits.title.main')}{" "}
                        <Box component="span" sx={{ color: theme.palette.secondary.main }}>
                            {t('benefits.title.accent')}
                        </Box>
                    </Typography>
                    <Typography
                        variant="h6"
                        sx={[
                            {
                                color: theme.palette.text.secondary,
                                maxWidth: 600,
                                mx: "auto",
                                lineHeight: 1.6,
                            },
                            (theme) =>
                                theme.applyStyles("dark", {
                                    color: "rgba(255, 255, 255, 0.8)",
                                }),
                        ]}
                    >
                        {t('benefits.subtitle')}
                    </Typography>
                </Box>

                {/* Benefits Grid */}
                <Box
                    role="list"
                    aria-label="Key benefits of using Furniturr"
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            md: "repeat(2, 1fr)",
                            lg: "repeat(3, 1fr)"
                        },
                        gap: 4,
                        position: "relative",
                        zIndex: 1,
                    }}
                >
                    {BENEFITS.map((benefit, index) => (
                        <Card
                            key={benefit.id}
                            role="listitem"
                            tabIndex={0}
                            aria-labelledby={`benefit-title-${benefit.id}`}
                            aria-describedby={`benefit-desc-${benefit.id} benefit-metric-${benefit.id}`}
                            sx={{
                                height: "100%",
                                transition: "all 0.3s ease",
                                border: 1,
                                borderColor: "divider",
                                position: "relative",
                                overflow: "hidden",
                                "&:hover, &:focus": {
                                    transform: "translateY(-8px)",
                                    boxShadow: theme.shadows[12],
                                    outline: "2px solid",
                                    outlineColor: theme.palette.primary.main,
                                    outlineOffset: "2px",
                                    "& .benefit-icon": {
                                        transform: "scale(1.1)",
                                    },
                                },
                                "&::before": {
                                    content: '""',
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    height: 4,
                                    background: `linear-gradient(90deg, ${theme.palette[benefit.color].main}, ${theme.palette[benefit.color].light})`,
                                },
                            }}
                        >
                            <CardContent sx={{ p: 4, height: "100%", display: "flex", flexDirection: "column" }}>
                                {/* Icon */}
                                <Avatar
                                    className="benefit-icon"
                                    aria-hidden="true"
                                    sx={{
                                        width: 64,
                                        height: 64,
                                        backgroundColor: theme.palette[benefit.color].main,
                                        color: theme.palette[benefit.color].contrastText,
                                        mb: 3,
                                        transition: "transform 0.3s ease",
                                        "& .MuiSvgIcon-root": {
                                            fontSize: "2rem",
                                        },
                                    }}
                                >
                                    {benefit.icon}
                                </Avatar>

                                {/* Title */}
                                <Typography
                                    variant="h6"
                                    component="h3"
                                    id={`benefit-title-${benefit.id}`}
                                    sx={[
                                        {
                                            mb: 2,
                                            fontWeight: 600,
                                            color: theme.palette.text.primary,
                                        },
                                        (theme) =>
                                            theme.applyStyles("dark", {
                                                color: "#FFFFFF",
                                            }),
                                    ]}
                                >
                                    {benefit.title}
                                </Typography>

                                {/* Description */}
                                <Typography
                                    variant="body1"
                                    id={`benefit-desc-${benefit.id}`}
                                    sx={[
                                        {
                                            mb: 3,
                                            color: theme.palette.text.secondary,
                                            lineHeight: 1.6,
                                            flexGrow: 1,
                                        },
                                        (theme) =>
                                            theme.applyStyles("dark", {
                                                color: "rgba(255, 255, 255, 0.8)",
                                            }),
                                    ]}
                                >
                                    {benefit.description}
                                </Typography>

                                {/* Metric */}
                                <Box
                                    role="note"
                                    aria-label="Benefit metric"
                                    sx={{
                                        p: 2,
                                        borderRadius: 1,
                                        backgroundColor: theme.palette.background.default,
                                        textAlign: "center",
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        id={`benefit-metric-${benefit.id}`}
                                        sx={{
                                            color: theme.palette[benefit.color].main,
                                            fontWeight: 700,
                                            fontSize: "1rem",
                                        }}
                                    >
                                        {benefit.metric}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    ))}
                </Box>

                {/* Call to Action */}
                <Box sx={{ textAlign: "center", mt: 8, position: "relative", zIndex: 1 }}>
                    <Typography
                        variant="h5"
                        sx={[
                            {
                                mb: 2,
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                            },
                            (theme) =>
                                theme.applyStyles("dark", {
                                    color: "#FFFFFF",
                                }),
                        ]}
                    >
                        {t('benefits.cta.title')}
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={[
                            {
                                color: theme.palette.text.secondary,
                                mb: 4,
                            },
                            (theme) =>
                                theme.applyStyles("dark", {
                                    color: "rgba(255, 255, 255, 0.8)",
                                }),
                        ]}
                    >
                        {t('benefits.cta.description')}
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
}
// #endregion

// #region Exports
export default LandingBenefitsOrganism;
export type { LandingBenefitsOrganismProps };
// #endregion
